import { ProjectCategoryRailV2 } from "@/components/navigation/ProjectCategoryNavV2";
import { TeamNavbarV3 } from "@/components/navigation/TeamNavbarV3";
import {
  NavLinkEnvironmentProvider,
  useGlobalNavEnvironment,
} from "@/components/navigation/nav-link-environment";
import { AmplitudeEvent, useAmplitudeTrackView } from "@/utils/amplitude";
import { ProjectNavbarLoadingSkeleton } from "@/components/navigation/ProjectNavbar";
import { useCategoryNavState } from "@/utils/hooks/use-category-nav-enabled";
import { useRouter } from "next/router";
import type { FC } from "react";

export interface TeamNavbarProps {
  activePage?: string;
  activeSubPage?: string;
}

/**
 * The environment a global settings page's nav points into, whether that nav is the category rail
 * there, and whether that is known yet (`ready`). LayoutV2 and the navbar read the same answer.
 */
export const useTeamNavbarUsesCategoryRail = () => {
  const environment = useGlobalNavEnvironment();
  const { enabled: usesCategoryRail, ready } = useCategoryNavState({
    scopedEnvironmentId: environment?.environmentId,
  });

  return { environment, usesCategoryRail, ready };
};

export const TeamNavbar: FC<TeamNavbarProps> = (props) => {
  const router = useRouter();
  const { environment, usesCategoryRail, ready } = useTeamNavbarUsesCategoryRail();
  // Every team-level page renders this navbar, so it is the one place to record a semantic
  // "team settings: view". `route` is the pathname template (e.g. /cloud-clusters/[clusterId]),
  // which separates index, new, detail, and edit pages that share an activePage.
  useAmplitudeTrackView(AmplitudeEvent.TEAM_SETTINGS_VIEW, router.pathname, {
    page: props.activePage,
    sub_page: props.activeSubPage,
    route: router.pathname,
  });

  // Neither navigation until the flag that picks one has loaded, so neither flashes.
  if (!ready) return <ProjectNavbarLoadingSkeleton label={"Loading navigation"} />;

  // The category rail opens on the global settings sidebar, which LayoutV2 renders beside it.
  return usesCategoryRail ? (
    <NavLinkEnvironmentProvider environment={environment}>
      <ProjectCategoryRailV2 globalSettings={{ activePage: props.activePage }} />
    </NavLinkEnvironmentProvider>
  ) : (
    <TeamNavbarV3 {...props} />
  );
};
